import React, { useState, useRef } from 'react';
import { X, Upload, FileText, CheckCircle2, AlertCircle, Loader2, AlertTriangle, GitMerge, UserPlus, SkipForward, Briefcase, Clock } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { batchImportResumes, resolveImport } from '../api';

const STAGE_LABELS: Record<string, string> = {
  new: '新候选人', screening: '筛选中', screening_passed: '筛选通过', screening_rejected: '筛选淘汰',
  first_interview: '一面', first_interview_rejected: '一面淘汰',
  second_interview: '二面', second_interview_rejected: '二面淘汰',
  final_interview: '终面', final_interview_rejected: '终面淘汰',
  offer: 'Offer', offer_rejected: 'Offer拒绝', pending_onboard: '待入职', onboarded: '已入职',
};

interface Props {
  positionId: string;
  positionName: string;
  positions?: { id: string; name: string }[];
  onClose: () => void;
  onImported: () => void;
}

interface ExistingCandidate {
  id: string;
  name: string;
  stage?: string;
  position_name?: string;
  latest_company?: string;
  latest_title?: string;
  source?: string;
  created_at?: string;
}

interface ImportResult {
  status: 'created' | 'duplicate' | 'error';
  fileName: string;
  candidateName?: string;
  error?: string;
  // duplicate 专有
  fileUrl?: string;
  matchLabel?: string;
  confidence?: 'strong' | 'weak';
  existing?: ExistingCandidate;
  parsed?: {
    name?: string;
    phone?: string;
    email?: string;
    education?: string;
    school?: string;
    work_years?: number;
    latest_company?: string;
    latest_title?: string;
  };
}

// 每条重复项的处理状态
type DupStatus = 'pending' | 'processing' | 'merged' | 'created' | 'skipped' | 'error';

type Phase = 'select' | 'importing' | 'done';

export default function BatchImportModal({ positionId, positionName, positions = [], onClose, onImported }: Props) {
  const [files, setFiles] = useState<File[]>([]);
  const [phase, setPhase] = useState<Phase>('select');
  const [results, setResults] = useState<ImportResult[]>([]);
  const [summary, setSummary] = useState({ total: 0, successCount: 0, duplicateCount: 0, errorCount: 0 });
  const [dupStates, setDupStates] = useState<Record<number, DupStatus>>({});
  const [targetPositionId, setTargetPositionId] = useState<string>(positionId || '');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeFile = (idx: number) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleImport = async () => {
    if (files.length === 0) return;
    if (!targetPositionId) { toast.error('请先选择导入到的岗位'); return; }
    setPhase('importing');
    try {
      const data = await batchImportResumes(files, targetPositionId);
      setResults(data.results);
      setSummary({ total: data.total, successCount: data.successCount, duplicateCount: data.duplicateCount || 0, errorCount: data.errorCount });
      setDupStates({});
      setPhase('done');
      if ((data.successCount || 0) > 0) onImported();
      if ((data.successCount || 0) === 0 && (data.duplicateCount || 0) === 0) {
        toast.error('全部导入失败，请检查文件格式');
      } else {
        toast.success(data.message);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || '批量导入失败');
      setPhase('select');
    }
  };

  // 重复项决策：合并到已有候选人 / 仍然新建 / 跳过
  const resolveDup = async (idx: number, r: ImportResult, action: 'merge' | 'create' | 'skip') => {
    if (!r.fileUrl) return;
    setDupStates(s => ({ ...s, [idx]: 'processing' }));
    try {
      await resolveImport({
        action,
        fileUrl: r.fileUrl,
        originalName: r.fileName,
        positionId: targetPositionId,
        targetId: action === 'merge' ? r.existing?.id : undefined,
      });
      setDupStates(s => ({ ...s, [idx]: action === 'merge' ? 'merged' : action === 'create' ? 'created' : 'skipped' }));
      if (action !== 'skip') onImported();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '处理失败');
      setDupStates(s => ({ ...s, [idx]: 'error' }));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files).filter(f =>
      ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(f.type)
    );
    if (dropped.length > 0) setFiles(prev => [...prev, ...dropped]);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 shrink-0">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-800">批量导入简历</h3>
            {positions.length > 0 ? (
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-xs text-gray-400 shrink-0">本批全部导入到岗位：</span>
                <select
                  value={targetPositionId}
                  onChange={e => setTargetPositionId(e.target.value)}
                  disabled={phase !== 'select'}
                  className="text-xs font-medium text-gray-700 border border-gray-200 rounded px-1.5 py-0.5 max-w-[220px] focus:outline-none focus:ring-1 focus:ring-primary-400 disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="">请选择岗位</option>
                  {positions.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <p className="text-xs text-gray-400 mt-0.5">导入到岗位：<span className="font-medium text-gray-600">{positionName}</span></p>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded"><X size={18} /></button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {phase === 'select' && (
            <div className="space-y-4">
              {/* Drop zone */}
              <div
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
                className="border-2 border-dashed border-gray-200 hover:border-primary-300 rounded-xl p-8 text-center cursor-pointer transition-colors group"
              >
                <Upload size={32} className="mx-auto text-gray-300 group-hover:text-primary-400 transition-colors" />
                <p className="mt-3 text-sm text-gray-500">点击选择或拖拽简历文件到此处</p>
                <p className="text-xs text-gray-400 mt-1">支持 PDF、DOC、DOCX，最多 50 个文件</p>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>

              {/* File list */}
              {files.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500">已选择 {files.length} 个文件</span>
                    <button onClick={() => setFiles([])} className="text-xs text-gray-500 hover:text-gray-600">清空</button>
                  </div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
                        <FileText size={14} className="text-gray-400 shrink-0" />
                        <span className="text-xs text-gray-600 flex-1 truncate">{f.name}</span>
                        <span className="text-[10px] text-gray-400 shrink-0">{(f.size / 1024).toFixed(0)}KB</span>
                        <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-gray-500 shrink-0">
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {phase === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={36} className="text-primary-500 animate-spin" />
              <p className="mt-4 text-sm font-medium text-gray-600">正在解析并导入 {files.length} 份简历...</p>
              <p className="text-xs text-gray-400 mt-1">请耐心等待，文件较多时需要一些时间</p>
            </div>
          )}

          {phase === 'done' && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="text-center flex-1">
                  <div className="text-xl font-bold text-gray-800">{summary.total}</div>
                  <div className="text-xs text-gray-400">总计</div>
                </div>
                <div className="text-center flex-1">
                  <div className="text-xl font-bold text-primary-600">{summary.successCount}</div>
                  <div className="text-xs text-primary-500">新增</div>
                </div>
                {summary.duplicateCount > 0 && (
                  <div className="text-center flex-1">
                    <div className="text-xl font-bold text-amber-500">{summary.duplicateCount}</div>
                    <div className="text-xs text-amber-500">疑似重复</div>
                  </div>
                )}
                {summary.errorCount > 0 && (
                  <div className="text-center flex-1">
                    <div className="text-xl font-bold text-gray-500">{summary.errorCount}</div>
                    <div className="text-xs text-gray-400">失败</div>
                  </div>
                )}
              </div>

              {/* 疑似重复：候选人卡片 + 合并/新建/跳过 */}
              {results.some(r => r.status === 'duplicate') && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-amber-600">
                    <AlertTriangle size={13} />
                    疑似重复，请逐条确认处理方式
                  </div>
                  <div className="space-y-2.5">
                    {results.map((r, i) => {
                      if (r.status !== 'duplicate') return null;
                      const st = dupStates[i] || 'pending';
                      const resolved = st === 'merged' || st === 'created' || st === 'skipped';
                      return (
                        <div key={i} className={clsx('rounded-xl border p-3', resolved ? 'border-gray-200 bg-gray-50/60' : 'border-amber-200 bg-amber-50/40')}>
                          {/* 文件 + 匹配依据 */}
                          <div className="flex items-center gap-1.5 mb-2">
                            <FileText size={13} className="text-gray-400 shrink-0" />
                            <span className="text-xs text-gray-600 truncate flex-1">{r.fileName}</span>
                            {r.matchLabel && (
                              <span className={clsx('text-[10px] px-1.5 py-0.5 rounded shrink-0',
                                r.confidence === 'strong' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500')}>
                                {r.matchLabel}{r.confidence === 'weak' ? ' · 疑似' : ''}
                              </span>
                            )}
                          </div>

                          {/* 已存在候选人卡片 */}
                          <div className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-800">{r.existing?.name || '已存在候选人'}</span>
                              {r.existing?.stage && (
                                <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">
                                  {STAGE_LABELS[r.existing.stage] || r.existing.stage}
                                </span>
                              )}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                              {r.existing?.position_name && (
                                <span className="inline-flex items-center gap-1"><Briefcase size={11} />{r.existing.position_name}</span>
                              )}
                              {(r.existing?.latest_company || r.existing?.latest_title) && (
                                <span>{[r.existing?.latest_company, r.existing?.latest_title].filter(Boolean).join(' · ')}</span>
                              )}
                              {r.existing?.source && <span>来源：{r.existing.source}</span>}
                              {r.existing?.created_at && (
                                <span className="inline-flex items-center gap-1"><Clock size={11} />{String(r.existing.created_at).slice(0, 10)}</span>
                              )}
                            </div>
                          </div>

                          {/* 本次简历解析信息 */}
                          <div className="mt-1.5 text-[11px] text-gray-400">
                            本次上传：{[r.parsed?.name, r.parsed?.phone, r.parsed?.latest_company, r.parsed?.school].filter(Boolean).join(' · ') || '（未解析出更多信息）'}
                          </div>

                          {/* 操作区 */}
                          <div className="mt-2.5">
                            {st === 'processing' ? (
                              <div className="flex items-center gap-1.5 text-xs text-gray-400"><Loader2 size={13} className="animate-spin" />处理中...</div>
                            ) : resolved ? (
                              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                <CheckCircle2 size={13} className="text-primary-500" />
                                {st === 'merged' ? '已合并到该候选人' : st === 'created' ? '已作为新候选人创建' : '已跳过（未导入）'}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => resolveDup(i, r, 'merge')}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
                                  <GitMerge size={12} /> 合并到该候选人
                                </button>
                                <button
                                  onClick={() => resolveDup(i, r, 'create')}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors">
                                  <UserPlus size={12} /> 仍然新建
                                </button>
                                <button
                                  onClick={() => resolveDup(i, r, 'skip')}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                  <SkipForward size={12} /> 跳过
                                </button>
                              </div>
                            )}
                            {st === 'error' && (
                              <div className="mt-1 text-[10px] text-red-500">处理失败，请重试</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 已新增 / 失败 列表 */}
              {results.some(r => r.status !== 'duplicate') && (
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {results.map((r, i) => {
                    if (r.status === 'duplicate') return null;
                    const ok = r.status === 'created';
                    return (
                      <div key={i} className={clsx('flex items-start gap-2 px-3 py-2 rounded-lg text-xs', ok ? 'bg-primary-50' : 'bg-gray-50')}>
                        {ok ? (
                          <CheckCircle2 size={14} className="text-primary-500 shrink-0 mt-0.5" />
                        ) : (
                          <AlertCircle size={14} className="text-gray-500 shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-700 truncate">{ok ? r.candidateName : r.fileName}</span>
                            {ok && r.fileName !== r.candidateName && (
                              <span className="text-gray-400 truncate">({r.fileName})</span>
                            )}
                          </div>
                          {ok && r.parsed && (
                            <div className="text-[10px] text-gray-400 mt-0.5">
                              {[r.parsed.latest_company, r.parsed.latest_title, r.parsed.education, r.parsed.school].filter(Boolean).join(' · ')}
                            </div>
                          )}
                          {r.status === 'error' && (
                            <div className="text-[10px] text-gray-400 mt-0.5">{r.error}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2 shrink-0">
          {phase === 'select' && (
            <>
              <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">
                取消
              </button>
              <button
                onClick={handleImport}
                disabled={files.length === 0 || !targetPositionId}
                className="px-5 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-40"
              >
                开始导入 ({files.length})
              </button>
            </>
          )}
          {phase === 'done' && (
            <button onClick={onClose} className="px-5 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
              完成
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
